/**
 * Viewer-side WebRTC black-box telemetry collector.
 *
 * Passively samples RTCPeerConnection.getStats() on the connection that RECEIVES the device's
 * video, plus lifecycle events, and emits one JSON payload per call. The payload is keyed so it
 * joins to the matching device-side black-box bundle by session_id.
 *
 * See design/webrtc-viewer-telemetry.md and lightfoot/ld_video/design/black-box-viewer-telemetry.md.
 *
 * Telemetry is purely observational: every entry point is wrapped so a failure here can NEVER
 * propagate into the WebRTC call. Listeners are additive (addEventListener) and removed on
 * teardown, so they never replace handlers registered elsewhere on the same connection.
 */

import WebRTCIssueDetector from 'webrtc-issue-detector'

const LOG_PREFIX = '[ViewerTelemetry]'
const SCHEMA_VERSION = 1
const POLL_INTERVAL_MS = 2000
const EMIT_AFTER_MS = 30000
const ISSUE_DETECTOR_INTERVAL_MS = 2000

// Silence the library's own logging; we surface what we care about under LOG_PREFIX.
const QUIET_LOGGER = { debug() {}, info() {}, warn() {}, error() {} }

// Fields to keep per stat type — names match getStats() exactly (camelCase), per spec §3.1.
const VIDEO_IN_FIELDS = [
  'packetsReceived', 'bytesReceived', 'packetsLost', 'jitter', 'framesReceived', 'framesDecoded',
  'framesDropped', 'keyFramesDecoded', 'frameWidth', 'frameHeight', 'framesPerSecond', 'pliCount',
  'firCount', 'nackCount', 'freezeCount', 'totalFreezesDuration', 'decoderImplementation', 'qpSum'
]
const AUDIO_IN_FIELDS = ['packetsReceived', 'bytesReceived', 'packetsLost', 'jitter', 'concealedSamples']
const AUDIO_OUT_FIELDS = ['packetsSent', 'bytesSent']
const CANDIDATE_PAIR_FIELDS = [
  'bytesReceived', 'bytesSent', 'packetsReceived', 'packetsSent', 'currentRoundTripTime',
  'availableIncomingBitrate', 'availableOutgoingBitrate', 'localCandidateId', 'remoteCandidateId'
]
const TRANSPORT_FIELDS = ['dtlsState', 'iceState', 'bytesReceived', 'bytesSent', 'packetsReceived', 'selectedCandidatePairId']
const CANDIDATE_FIELDS = ['address', 'port', 'protocol', 'candidateType']

// Returns { ...fields } with each requested key coerced to its value or null, or null if obj is absent.
function pluck(obj, fields) {
  if (!obj) return null
  const out = {}
  for (const key of fields) {
    out[key] = obj[key] === undefined ? null : obj[key]
  }
  return out
}

export class ViewerTelemetry {
  #pc
  #ctx
  #t0
  #payload
  #pollTimer = null
  #emitTimer = null
  #issueDetector = null
  #listeners = []
  #teardownHandlers = []
  #emitted = false

  /**
   * @param {RTCPeerConnection} pc - the connection receiving the device's video
   * @param {object} ctx - { session_id, telemetry_id, device_id, peer_id, channel, camera, app_version, emit }
   */
  constructor(pc, ctx) {
    this.#pc = pc
    this.#ctx = ctx
    this.#t0 = performance.now()
    this.#payload = {
      schema_version: SCHEMA_VERSION,
      role: 'viewer',
      session_id: ctx.session_id,
      telemetry_id: ctx.telemetry_id,
      device_id: ctx.device_id,
      peer_id: ctx.peer_id,
      channel: ctx.channel,
      camera: ctx.camera,
      viewer: { app_version: ctx.app_version, user_agent: navigator.userAgent },
      wall_clock_start: new Date().toISOString(),
      wall_clock_end: null,
      outcome: 'failed_to_connect',
      media_summary: {},
      final_states: {},
      selected_candidate_pair: null,
      events: [{ t_ms: 0, event: 'pc_created' }],
      // Quality issues flagged by the webrtc-issue-detector library (e.g. decoder problems,
      // network throughput, frequent PLI/NACK) — the same signals shown in the quality indicator.
      issues: [],
      samples: []
    }

    try {
      this.#attachListeners()
      this.#startIssueDetector()
      this.#pollTimer = setInterval(() => this.#poll(), POLL_INTERVAL_MS)
      this.#emitTimer = setTimeout(() => this.finish(), EMIT_AFTER_MS)
      this.#attachTeardownHandlers()
      console.info(`${LOG_PREFIX} enabled — collecting viewer WebRTC stats`, {
        session_id: ctx.session_id,
        telemetry_id: ctx.telemetry_id,
        device_id: ctx.device_id,
        channel: ctx.channel,
        camera: ctx.camera,
        poll_interval_ms: POLL_INTERVAL_MS,
        emit_after_ms: EMIT_AFTER_MS
      })
    } catch (err) {
      console.warn(`${LOG_PREFIX} failed to start`, err)
    }
  }

  #now() {
    return Math.round(performance.now() - this.#t0)
  }

  #pushEvent(event, value, detail) {
    try {
      const entry = { t_ms: this.#now(), event }
      if (value !== undefined) entry.value = value
      if (detail !== undefined) entry.detail = detail
      this.#payload.events.push(entry)
    } catch (err) {
      console.warn('ViewerTelemetry: event capture failed', err)
    }
  }

  #addListener(target, type, handler) {
    target.addEventListener(type, handler)
    this.#listeners.push({ target, type, handler })
  }

  #attachListeners() {
    const pc = this.#pc
    this.#addListener(pc, 'connectionstatechange', () => this.#pushEvent('connectionstatechange', pc.connectionState))
    this.#addListener(pc, 'iceconnectionstatechange', () => this.#pushEvent('iceconnectionstatechange', pc.iceConnectionState))
    this.#addListener(pc, 'icegatheringstatechange', () => this.#pushEvent('icegatheringstatechange', pc.iceGatheringState))
    this.#addListener(pc, 'signalingstatechange', () => this.#pushEvent('signalingstatechange', pc.signalingState))
    this.#addListener(pc, 'track', (e) => this.#pushEvent('track', e.track?.kind))
    this.#addListener(pc, 'icecandidateerror', (e) =>
      this.#pushEvent('icecandidateerror', undefined, {
        errorCode: e.errorCode ?? null, errorText: e.errorText ?? null, url: e.url ?? null,
        address: e.address ?? null, port: e.port ?? null
      }))
  }

  // Flush on real page teardown. We intentionally do NOT listen for visibilitychange→hidden:
  // that fires on benign tab-switch/minimize/screen-lock, and finish() is terminal, so it would
  // truncate collection for calls that keep running. Emission is: 30s, session end, or pagehide.
  #attachTeardownHandlers() {
    const onHide = () => this.finish()
    window.addEventListener('pagehide', onHide)
    this.#teardownHandlers.push({ target: window, type: 'pagehide', handler: onHide })
  }

  // Runs the webrtc-issue-detector library on our connection so the payload includes the same
  // quality issues (decoder problems, network throughput, frequent PLI/NACK, etc.) that the UI
  // quality indicator surfaces. Independent of the UI watcher so it runs whenever telemetry does.
  #startIssueDetector() {
    // Self-contained: a detector failure must not disable the core stats sampling.
    try {
      this.#issueDetector = new WebRTCIssueDetector({
        onIssues: (issues) => this.#recordIssues(issues),
        onNetworkScoresUpdated: () => {},
        autoAddPeerConnections: false,
        getStatsInterval: ISSUE_DETECTOR_INTERVAL_MS,
        logger: QUIET_LOGGER
      })
      this.#issueDetector.handleNewPeerConnection(this.#pc)
    } catch (err) {
      this.#issueDetector = null
      console.warn(`${LOG_PREFIX} issue detector failed to start`, err)
    }
  }

  #recordIssues(issues) {
    try {
      if (!Array.isArray(issues) || issues.length === 0) return
      this.#payload.issues.push({ t_ms: this.#now(), issues })
      console.info(`${LOG_PREFIX} issues detected`, issues.map(i => `${i.type}/${i.reason}`))
    } catch (err) {
      console.warn(`${LOG_PREFIX} issue capture failed`, err)
    }
  }

  async #poll() {
    try {
      const report = await this.#pc.getStats()
      const values = [...report.values()]
      const byType = (type, kind) => values.find(s => s.type === type && (!kind || s.kind === kind))

      let pair = values.find(s => s.type === 'candidate-pair' && s.nominated && s.state === 'succeeded')
      if (!pair) {
        const transport = byType('transport')
        if (transport?.selectedCandidatePairId) {
          pair = values.find(s => s.id === transport.selectedCandidatePairId)
        }
      }

      const sample = { t_ms: this.#now() }
      const videoIn = pluck(byType('inbound-rtp', 'video'), VIDEO_IN_FIELDS)
      const audioIn = pluck(byType('inbound-rtp', 'audio'), AUDIO_IN_FIELDS)
      const audioOut = pluck(byType('outbound-rtp', 'audio'), AUDIO_OUT_FIELDS)
      const candidatePair = pluck(pair, CANDIDATE_PAIR_FIELDS)
      const transport = pluck(byType('transport'), TRANSPORT_FIELDS)
      // Omit absent sub-objects entirely — an absent video_in across the whole call is meaningful.
      if (videoIn) sample.video_in = videoIn
      if (audioIn) sample.audio_in = audioIn
      if (audioOut) sample.audio_out = audioOut
      if (candidatePair) sample.candidate_pair = candidatePair
      if (transport) sample.transport = transport
      this.#payload.samples.push(sample)

      if (pair && !this.#payload.selected_candidate_pair) {
        const local = values.find(s => s.id === pair.localCandidateId)
        const remote = values.find(s => s.id === pair.remoteCandidateId)
        this.#payload.selected_candidate_pair = {
          local: pluck(local, CANDIDATE_FIELDS),
          remote: pluck(remote, CANDIDATE_FIELDS)
        }
      }

      console.debug(`${LOG_PREFIX} sample #${this.#payload.samples.length} @${sample.t_ms}ms`, {
        video_packetsReceived: videoIn ? videoIn.packetsReceived : 'no video_in',
        video_framesDecoded: videoIn ? videoIn.framesDecoded : null,
        audio_packetsReceived: audioIn ? audioIn.packetsReceived : null
      })
    } catch (err) {
      console.warn(`${LOG_PREFIX} getStats poll failed`, err)
    }
  }

  #summarize() {
    const samples = this.#payload.samples
    const lastVideo = [...samples].reverse().find(s => s.video_in)
    return {
      video_received: samples.some(s => s.video_in && s.video_in.packetsReceived > 0),
      video_frames_decoded: lastVideo?.video_in?.framesDecoded ?? 0,
      audio_received: samples.some(s => s.audio_in && s.audio_in.packetsReceived > 0),
      audio_sent: samples.some(s => s.audio_out && s.audio_out.packetsSent > 0)
    }
  }

  #cleanup() {
    if (this.#pollTimer) { clearInterval(this.#pollTimer); this.#pollTimer = null }
    if (this.#emitTimer) { clearTimeout(this.#emitTimer); this.#emitTimer = null }
    if (this.#issueDetector) {
      try { this.#issueDetector.stopWatchingNewPeerConnections() } catch (err) { console.warn(`${LOG_PREFIX} issue detector stop failed`, err) }
      this.#issueDetector = null
    }
    for (const { target, type, handler } of this.#listeners) {
      target.removeEventListener(type, handler)
    }
    this.#listeners = []
    for (const { target, type, handler } of this.#teardownHandlers) {
      target.removeEventListener(type, handler)
    }
    this.#teardownHandlers = []
  }

  // Stop collecting without emitting.
  stop() {
    try {
      this.#cleanup()
    } catch (err) {
      console.warn(`${LOG_PREFIX} stop failed`, err)
    }
  }

  // Finalize the payload and emit it exactly once. Safe to call multiple times.
  // The transport always uses keepalive so emits triggered during page unload (the session's
  // beforeunload→disconnect path fires before our pagehide handler) still survive the unload.
  finish() {
    if (this.#emitted) return
    this.#emitted = true
    try {
      this.#cleanup()
      const pc = this.#pc
      this.#payload.wall_clock_end = new Date().toISOString()
      this.#payload.outcome = pc.connectionState === 'connected' ? 'connected' : 'failed_to_connect'
      this.#payload.final_states = {
        connection: pc.connectionState,
        ice: pc.iceConnectionState,
        signaling: pc.signalingState,
        dtls: this.#payload.samples.at(-1)?.transport?.dtlsState ?? null
      }
      this.#payload.media_summary = this.#summarize()
      console.info(`${LOG_PREFIX} emitting payload`, {
        session_id: this.#payload.session_id,
        telemetry_id: this.#payload.telemetry_id,
        outcome: this.#payload.outcome,
        samples: this.#payload.samples.length,
        media_summary: this.#payload.media_summary
      })
      this.#ctx.emit(this.#payload)
    } catch (err) {
      console.warn(`${LOG_PREFIX} finish failed`, err)
    }
  }
}
